// /src/renderer/analysis/AnalysisPageSlice.ts
import {
  AnalysisState,
  AnalysisUIConfig,
  CornerstoneConfig,
  LayoutState,
  PanelState,
  PanelStates,
  PresetType,
  SelectedAnalysis,
  SelectedTool,
  SwitchConfigs,
  SwitchConfigType,
  ThicknessMethodType,
  ToolState,
  DirectionType,
  SegmentationProps,
  SegmentationControllerType,
  PickPoints,
  ChamberViewProps,
  defaultWindowCenter,
  defaultWindowWidth,
  defaultPreset,
  AnalysisType,
  AnalysisDefault,
} from 'config/analysisConfig';
import {
  PANEL_COMPONENT_KEY_NAMES,
  PANEL_NAMES,
  TOOL_GROUP_NAMES,
} from 'config/labels';
import {
  DEFAULT_LAYOUT_CONFIG_INDEX,
  EMPTY_DICOM_LAYOUT_CONFIG,
  LAYOUT_CONFIG,
} from 'config/layoutConfig';
import {
  PANEL_CONFIG,
  PanelComponentKeyType,
  PanelType,
  PanelConfig,
} from 'config/panelConfig';
import { ToolGroupType, exclusiveToolByPanel } from 'config/toolsConfig';
import { Segmentation } from '@cornerstonejs/tools/dist/esm/types';
import {
  createSlice,
  PayloadAction,
  PayloadActionCreator,
} from '@reduxjs/toolkit';
import { RootState } from '@root/rootReducer';
import { NumberField } from 'react-aria-components';
import { ToolId, ToolCategoryType } from 'config/toolDefinitionsConfig';
import { TOOL_REGISTRY } from 'config/toolRegistry';
import { configStore } from 'config/configStore';

export interface panelToolStateV2 {
  activeToolId?: ToolId;
  activeGroup?: ToolCategoryType;
}

export interface globalToolStateV2 {
  toggles: Partial<Record<ToolId, boolean>>;
}

export interface ToolStateV2 {
  panels: Record<PanelType, panelToolStateV2>;
  global: globalToolStateV2;
}

export const getDefaultGlobalToggleValue = (id: ToolId): boolean => {
  switch (id) {
    case 'ToggleDicomZoomSync':
    case 'ToggleDicomPanSync':
      return true;
    default:
      return false;
  }
};

const initialToolStatesV2: ToolStateV2 = {
  panels: {
    EmptyPanel: { activeToolId: undefined, activeGroup: undefined },
    DICOMPanel: { activeToolId: 'Scroll', activeGroup: 'navigation' },
    VRPanel: { activeToolId: 'Rotation', activeGroup: 'navigation' },
    Surface3DPanel: { activeToolId: 'Rotation', activeGroup: 'navigation' },
    CPRPanel: { activeToolId: 'Scroll', activeGroup: 'navigation' },
    CrossSectionPanel: { activeToolId: 'Scroll', activeGroup: 'navigation' },
    ReportPanel: { activeToolId: undefined, activeGroup: undefined },
    PlotPanel: { activeToolId: undefined, activeGroup: undefined },
  },
  global: {
    toggles: {
      ToggleAxes: configStore.get().annotationState.Axis,
      ToggleDICOMTags: false,
      ToggleMeasurementVisibility:
        configStore.get().annotationState.Measurement,
      ToggleAnnotations: configStore.get().annotationState.Annotations,
      ToggleSegmentationManager: false,
      TogglePatientData: configStore.get().annotationState.PatientInfo,
      ToggleDicomZoomSync: true,
      ToggleDicomPanSync: true,
      // ToggleSync: false,
      ToggleHeartCut: false,
      ToggleMIP: false,
      TogglePlaqueManual:false,
    },
  },
};

export type MainIndex = {
  index: number;
  branchId: number;
};
export type IndexedState = {
  mainIndex: MainIndex;
  pickPoint: PickPoints;
};
export type IndexedStates = Record<number, PickPoints[]>;
export type CPRStateBatch = {
  mainIndex?: MainIndex;
  mainIndices?: [number, number, number];
  indexedState?: IndexedState;
  indexedStates?: IndexedStates;
};

const initialSelectedTool: SelectedTool = {
  id: 'scroll',
  groupName: 'navigation',
};

const initialSelectedTool2: SelectedTool = {
  id: 'rotation',
  groupName: 'navigation',
};

const initialSelectedTool3: SelectedTool = {
  id: '',
  groupName: 'blank',
};

const initialToolStateItem: ToolState = {
  selectedTool: null,
  activatedTools: {},
  exclusiveTool: initialSelectedTool,
  // recentlyUsedTool: {} as Record<ToolGroupType, string>,
};

const initialToolState: Record<PanelType, ToolState> = (
  Object.keys(PANEL_NAMES) as PanelType[]
).reduce(
  (acc, key) => {
    acc[key] = {
      ...initialToolStateItem,
      exclusiveTool:
        key === 'VRPanel' || key === 'Surface3DPanel'
          ? initialSelectedTool2
          : key === 'EmptyPanel'
            ? initialSelectedTool3
            : initialSelectedTool,
    };
    return acc;
  },
  {} as Record<PanelType, ToolState>,
);
const initialPanelState: Record<PanelComponentKeyType, PanelState> = (
  Object.keys(PANEL_COMPONENT_KEY_NAMES) as PanelComponentKeyType[]
).reduce(
  (acc, key) => {
    acc[key] = {};
    return acc;
  },
  {} as Record<PanelComponentKeyType, PanelState>,
);

const initialSwitchConfigType: SwitchConfigType = {
  switchAnnotation: false,
  switchMeasurement: false,
  switchPatientData: false,
  switchDICOMTag: false,

  switchCine: false,
  switchLockAxisCenter: false,
  switchSynchronizingPanel: false,
  switchClippingPlane: false,
  switchGrid: false,
  switchSegmentManagerTools: false,
  // switchCTNegativeIntensity: false,
  switchViewReport: false,
  switchAxes:false,
};
const initialLayoutState: LayoutState = {
  layoutConfig: [],
  switchConfigs: initialSwitchConfigType,
  updateData: false,
  loading: true,
};
const initialUIConfig: AnalysisUIConfig = {
  selectedLayoutConfig: EMPTY_DICOM_LAYOUT_CONFIG,
  layoutState: initialLayoutState,
  voi:{
    windowLevel:defaultWindowCenter,
    windowWidth:defaultWindowWidth
  },
  preset:defaultPreset
};
const initialCornerstoneConfig: CornerstoneConfig = {
  renderingEngineId: [],
  initialized: false,
};

const initialState: AnalysisState = {
  selectedAnalysis: null,
  uiConfig: initialUIConfig,

  toolStates: initialToolState,
  panelStates: initialPanelState,
  cornerstoneConfig: initialCornerstoneConfig,

  toolStatesV2: initialToolStatesV2,

  analysisDefaults:{},
};

/**
 * action 명명
 * set: 상태를 특정 값으로 설정할 때 (예: setSelectedTool)
 * update: 기존 상태의 일부를 갱신할 때 (예: updateUserProfile)
 * add: 새로운 항목을 추가할 때 (예: addTodo)
 * remove 또는 delete: 항목을 제거할 때 (예: removeItem, deletePost)
 * toggle: Boolean 상태를 반전시킬 때 (예: toggleSidebar)
 * fetch, request: 데이터를 요청할 때 (예: fetchDataRequest)
 * receive: 데이터를 성공적으로 받아왔을 때 (예: receiveData)
 * fail: 실패한 결과를 처리할 때 (예: fetchDataFail)
 * clear 또는 reset: 상태를 초기화하거나 지울 때 (예: clearNotifications, resetForm)
 */

const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    setSelectedAnalysis(state, action: PayloadAction<SelectedAnalysis>) {
      // const unequalSeries = action.payload.seriesId !== state.selectedAnalysis?.seriesId;

      state.selectedAnalysis = action.payload;
      // Series만 선택시, analysisTypeId 강제로 singleDICOM 주입
      if (!state.selectedAnalysis) return;

      if (!state.selectedAnalysis?.analysisType) {
        // constant 만들어야.
        state.selectedAnalysis.analysisType = 'preview';
        state.uiConfig.layoutState.loading = true;
      }

      // 분석 layout 초기 로드
      const analysisType = state.selectedAnalysis.analysisType;
      const initialLayoutIndex = DEFAULT_LAYOUT_CONFIG_INDEX[analysisType];
      const layoutConfigs = LAYOUT_CONFIG[analysisType];

      state.uiConfig = {
        ...state.uiConfig,
        selectedLayoutConfig: layoutConfigs[initialLayoutIndex],
        layoutState: {
          // ...initialLayoutState,
          ...state.uiConfig.layoutState,
          layoutConfig: layoutConfigs, // 가능한 layout 목록
          selectedPanelConfig: layoutConfigs[initialLayoutIndex].panelConfig[0], // 강제로 0번
          updateData: false,
          // SwitchConfigs: state.uiConfig.layoutState.SwitchConfigs,// 그대로 가져감
        },
        // commonSeriesInfo:(unequalSeries?{}:state.uiConfig.commonSeriesInfo)
        // dirty:unequalSeries
      };

      // 패널 정보 로드? 필요한가?
      state.uiConfig.layoutState.layoutConfig[
        initialLayoutIndex
      ].panelConfig.map((panelConfig, index) => {
        //panelConfig.
      });
    },

    // setSelectedTool(state, action: PayloadAction<SelectedTool>) {
    //   const tool = action.payload;

    //   if (!state.uiConfig.layoutState.selectedPanelConfig) return;
    //   // 현재 선택된 패널의 toolStates를 가져온다
    //   const currentPanelTool = {...state.toolStates[state.uiConfig.layoutState.selectedPanelConfig.panelId]};

    //   if (tool.isExclusive) {
    //     const panels = (Object.keys(exclusiveToolByPanel) as PanelType[]).filter(
    //       (panelType) => exclusiveToolByPanel[panelType].includes(tool.id)
    //     );
    //     for (const panelType of panels) {
    //       state.toolStates[panelType].exclusiveTool = tool;
    //     }
    //   } else if (tool.isToggleable) {
    //     currentPanelTool.activatedTools[tool.id] = currentPanelTool.activatedTools[tool.id] !== true;
    //   } else {
    //     currentPanelTool.selectedTool = tool;
    //   }

    //   // // update used tool
    //   // if (TOOL_GROUP_NAMES.recommended !== tool.groupName) {
    //   //   // recommended tool 카운팅 ㄴㄴ
    //   //   currentPanelTool.recentlyUsedTool[tool.groupName] = tool.id;
    //   // }
    //   state.toolStates[
    //     state.uiConfig.layoutState.selectedPanelConfig.panelId
    //   ] = { ...currentPanelTool };
    // },
    setSelectedTool(state, action: PayloadAction<SelectedTool>) {
      const tool = action.payload;
      const selectedPanel = state.uiConfig.layoutState.selectedPanelConfig;
      if (!selectedPanel) return;

      const panelId = selectedPanel.panelId;
      const currentPanelTool = state.toolStates[panelId];

      if (tool.isExclusive) {
        for (const [panelType, tools] of Object.entries(exclusiveToolByPanel)) {
          if (tools.includes(tool.id)) {
            state.toolStates[panelType as PanelType].exclusiveTool = tool;
          }
        }
      } else if (tool.isToggleable) {
        currentPanelTool.activatedTools[tool.id] =
          !currentPanelTool.activatedTools[tool.id];
      } else {
        currentPanelTool.selectedTool = tool;
      }

      // 필요 시 최근 사용 툴 업데이트
      // updateRecentlyUsedTool(currentPanelTool, tool);
    },

    setActivatedTool(
      state,
      action: PayloadAction<{ tool: SelectedTool; check: boolean }>,
    ) {
      const { tool, check } = action.payload;
      if (
        state.uiConfig.layoutState.selectedPanelConfig?.panelId &&
        tool.isToggleable
      ) {
        let currentActivatedTool =
          state.toolStates[
            state.uiConfig.layoutState.selectedPanelConfig.panelId
          ].activatedTools;
        currentActivatedTool[tool.id] = check;
        //지우진 말까?
        // if(currentActivatedTool.some((item)=>item.id === tool.id)){
        //   currentActivatedTool = currentActivatedTool.filter((item)=>item.id !== tool.id);
        // }else
        // {
        //   currentActivatedTool.push(tool)
        // }
        state.toolStates[
          state.uiConfig.layoutState.selectedPanelConfig.panelId
        ].activatedTools = currentActivatedTool;
      }
    },
    // setRecentlyUsedTool(state, action: PayloadAction<[ToolGroupType,string]>){
    //   const groupId = action.payload;
    //   if(state.uiConfig.layoutState.selectedPanelConfig?.panelId){
    //     state.toolStates[state.uiConfig.layoutState.selectedPanelConfig.panelId].recentlyUsedTool[groupId[0]] = groupId[1];
    //   }
    // },

    // updateRecentlyUsedToolsList(state, action: PayloadAction<RecentlyUsedTool[]>){
    //   state.recentlyUsedTool = action.payload;
    // },

    setCurrentLayout(state, action: PayloadAction<string>) {
      const selectedType = action.payload;

      const selectedLayout = state.uiConfig.layoutState.layoutConfig.find(
        (item) => item.type === selectedType,
      );
      // 선택한거로 바꿔주고
      if (selectedLayout) state.uiConfig.selectedLayoutConfig = selectedLayout;
      // 풀스크린 꺼주고
      state.uiConfig.layoutState.fullscreenPanelComponentKey = undefined;
    },
    setFullscreenPanelComponentKey(
      state,
      action: PayloadAction<PanelComponentKeyType>,
    ) {
      state.uiConfig.layoutState.fullscreenPanelComponentKey = action.payload;
    },

    // setCommonSeriesInfo(state,action : PayloadAction<Partial<Record<string,string[]|string>>>){

    //   const info = action.payload;
    //   state.uiConfig.commonSeriesInfo = {
    //     ...state.uiConfig.commonSeriesInfo,
    //     ...info
    //   };
    // },

    setSelectedPanelState(state, action: PayloadAction<PanelState>) {
      if (state.uiConfig.layoutState.selectedPanelConfig?.componentKey)
        state.panelStates[
          state.uiConfig.layoutState.selectedPanelConfig.componentKey
        ] = action.payload;
    },
    setLayoutStateLoading(state, action: PayloadAction<boolean>) {
      state.uiConfig.layoutState.loading = action.payload;
    },
    setLayoutStateUpdateData(state, action: PayloadAction<boolean>) {
      state.uiConfig.layoutState.updateData = action.payload;
    },
    updateSelectedLayoutPanelConfig(
      state,
      action: PayloadAction<PanelComponentKeyType[]>,
    ) {
      const [srcComponentKey, destComponentKey] = action.payload;
      const panelConfig = state.uiConfig.selectedLayoutConfig.panelConfig;
      if (srcComponentKey !== destComponentKey) {
        const srcIndex = panelConfig.findIndex(
          (panelConfig) => panelConfig.componentKey == srcComponentKey,
        );
        const destIndex = panelConfig.findIndex(
          (panelConfig) => panelConfig.componentKey == destComponentKey,
        );
        const srcPanelConfig = panelConfig[srcIndex];

        // 패널이 열려있으면, 두 패널을 스왑
        if (destIndex !== -1) {
          panelConfig[srcIndex] = panelConfig[destIndex];
          panelConfig[destIndex] = srcPanelConfig;
        } else {
          // 목적지 패널의 속성을 탐지하고 변경
          panelConfig[srcIndex].componentKey = destComponentKey;
          if (state.selectedAnalysis?.analysisType !== 'preview') {
            //나머진 상수 주입
            switch (destComponentKey) {
              case 'Axial':
              case 'Coronal':
              case 'Sagittal':
                panelConfig[srcIndex].panelId = 'DICOMPanel';
                break;
              case 'VR':
                panelConfig[srcIndex].panelId = 'VRPanel';
                break;
              case 'Surface3D':
                panelConfig[srcIndex].panelId = 'Surface3DPanel';
                break;
              case 'Report':
                panelConfig[srcIndex].panelId = 'ReportPanel';
                break;
            }
          }
        }
        state.uiConfig.selectedLayoutConfig.panelConfig = panelConfig;
      }
    },
    updatePanelStates(state, action: PayloadAction<Partial<PanelStates>>) {
      state.panelStates = {
        ...state.panelStates,
        ...action.payload,
      };

      // (state: RootState) => state.analysis.panelStates,
    },

    updatePanelStateThickness(
      state,
      action: PayloadAction<[PanelComponentKeyType, number]>,
    ) {
      const [componentKey, thickness] = action.payload;
      state.panelStates[componentKey].thickness = thickness;
    },
    updatePanelStateThicknessMethod(
      state,
      action: PayloadAction<[PanelComponentKeyType, ThicknessMethodType]>,
    ) {
      const [componentKey, thicknessMethod] = action.payload;
      state.panelStates[componentKey].thicknessMethod = thicknessMethod;
    },
    updatePanelStateZoom(
      state,
      action: PayloadAction<[PanelComponentKeyType, number]>,
    ) {
      const [componentKey, zoom] = action.payload;
      state.panelStates[componentKey].zoom = zoom;
    },
    // updatePanelStateWindowLevel(
    //   state,
    //   action: PayloadAction<[PanelComponentKeyType, number]>,
    // ) {
    //   const [componentKey, windowLevel] = action.payload;
    //   state.panelStates[componentKey].windowLevel = windowLevel;
    // },
    // updatePanelStateWindowWidth(
    //   state,
    //   action: PayloadAction<[PanelComponentKeyType, number]>,
    // ) {
    //   const [componentKey, windowWidth] = action.payload;
    //   state.panelStates[componentKey].windowWidth = windowWidth;
    // },
    // updatePanelStateWindowLevelWidth(
    //   state,
    //   action: PayloadAction<[PanelComponentKeyType, number, number]>,
    // ) {
    //   const [componentKey, windowLevel, windowWidth] = action.payload;
    //   state.panelStates[componentKey].windowLevel = windowLevel;
    //   state.panelStates[componentKey].windowWidth = windowWidth;
    // },
    updatePanelStateSpacing(
      state,
      action: PayloadAction<[PanelComponentKeyType, number]>,
    ) {
      const [componentKey, spacing] = action.payload;
      if (state.panelStates[componentKey].chamberViewProps) {
        state.panelStates[componentKey].chamberViewProps.spacing = spacing;
      }
    },
    updatePanelStateImageSize(
      state,
      action: PayloadAction<[PanelComponentKeyType, number]>,
    ) {
      const [componentKey, imageSize] = action.payload;
      if (state.panelStates[componentKey].chamberViewProps) {
        state.panelStates[componentKey].chamberViewProps.imageSize = imageSize;
      }
    },
    updatePanelStateSliceIndex(
      state,
      action: PayloadAction<[PanelComponentKeyType, number]>,
    ) {
      const [componentKey, sliceIndex] = action.payload;
      state.panelStates[componentKey].sliceIndex = sliceIndex;
    },
    updatePanelStatePosition(
      state,
      action: PayloadAction<[PanelComponentKeyType, [number, number, number]]>,
    ) {
      const [componentKey, position] = action.payload;
      state.panelStates[componentKey].position = position;
    },
    // 반드시 CT패널 모두 들어옴. 따라서,,
    // updatePanelStatePreset(
    //   state,
    //   action: PayloadAction<
    //     [PanelComponentKeyType, Record<string, PresetType>]
    //   >,
    // ) {
    //   const [_, preset] = action.payload;
    //   const keys: PanelComponentKeyType[] = [
    //     'Axial',
    //     'Coronal',
    //     'Sagittal',
    //     'VR',
    //   ];
    //   keys.map((componentKey) => {
    //     if (state.panelStates[componentKey].preset) {
    //       //이것은 삭제 시그널
    //       const key = Object.keys(preset)[0];
    //       if (key[0] === 'd') {
    //         delete state.panelStates[componentKey].preset[key.slice(1)];
    //       } else {
    //         state.panelStates[componentKey].preset = {
    //           ...state.panelStates[componentKey].preset,
    //           ...preset,
    //         };
    //       }
    //     }
    //   });
    //   // [preset.label] = preset;
    // },

    //무조건 여기 패널스테이트를 참고한다.
    updatePanelStateSegmentation(
      state,
      action: PayloadAction<
        [SegmentationControllerType, Partial<SegmentationProps>]
      >,
    ) {
      const [type, props] = action.payload;
      if (state.panelStates.Surface3D.segmentation) {
        state.panelStates.Surface3D.segmentation[type] = {
          ...state.panelStates.Surface3D.segmentation[type],
          ...props,
        };
      }
    },
    updatePanelStateDirection(
      state,
      action: PayloadAction<[PanelComponentKeyType, DirectionType]>,
    ) {
      const [componentKey, direction] = action.payload;
      state.panelStates[componentKey].direction = direction;
    },
    updatePanelStateChamberViewProps(
      state,
      action: PayloadAction<[PanelComponentKeyType, ChamberViewProps]>,
    ) {
      const [componentKey, chamberViewProps] = action.payload;
      state.panelStates[componentKey].chamberViewProps = {
        ...state.panelStates[componentKey].chamberViewProps,
        ...chamberViewProps,
      };
    },

    updatePanelStateCoronary(
      state,
      action: PayloadAction<
        [PanelComponentKeyType, Partial<typeof initialPanelState.CPR.coronary>]
      >,
    ) {
      const [componentKey, updates] = action.payload;
      const prev = state.panelStates.CPR.coronary;
      console.log(updates)
      if (updates) {
        state.panelStates.CPR.coronary = {
          ...prev,
          current: updates.hasOwnProperty('current')
            ? updates.current!
            : (prev?.current ?? [0, 0, 0]),
          state: updates.hasOwnProperty('state')
            ? updates.state!
            : (prev?.state ?? {}),
          currentCPRId: updates.hasOwnProperty('currentCPRId')
            ? updates.currentCPRId!
            : (prev?.currentCPRId ?? 0),
        };
      }
    },
    updateCPRStateMainIndex(state, action: PayloadAction<MainIndex>) {
      const { index, branchId } = action.payload;
      state.panelStates['CPR'].coronary!.current[index] = branchId;
    },
    updateCPRStateBatch(state, action: PayloadAction<CPRStateBatch>) {
      const coronary = state.panelStates['CPR'].coronary!;
      const { mainIndex, mainIndices, indexedState, indexedStates } =
        action.payload;

      if (mainIndices) {
        coronary.current = mainIndices;
      }

      if (indexedStates) {
        Object.entries(indexedStates).forEach(([branchId, pickPoints]) => {
          coronary.state[Number(branchId)] = pickPoints;
        });
      }

      if (mainIndex) {
        coronary.current[mainIndex.index] = mainIndex.branchId;
        coronary.currentCPRId = mainIndex.branchId;
      }

      if (indexedState) {
        const { mainIndex: indexedMainIndex, pickPoint } = indexedState;
        if (!coronary.state[indexedMainIndex.branchId]) {
          coronary.state[indexedMainIndex.branchId] = [];
        }
        coronary.state[indexedMainIndex.branchId][indexedMainIndex.index] =
          pickPoint;
        coronary.currentCPRId = indexedMainIndex.branchId;
      }
    },
    updateCPRStateMainIndices(
      state,
      action: PayloadAction<[number, number, number]>,
    ) {
      const indices = action.payload;
      state.panelStates['CPR'].coronary!.current = indices;
    },
    updateCPRStateIndexedState(state, action: PayloadAction<IndexedState>) {
      const { mainIndex, pickPoint } = action.payload;
      if (!state.panelStates['CPR'].coronary!.state[mainIndex.branchId]) {
        state.panelStates['CPR'].coronary!.state[mainIndex.branchId] = [];
      }
      state.panelStates['CPR'].coronary!.state[mainIndex.branchId][
        mainIndex.index
      ] = pickPoint; // 반드시 있을거라는 보장
      state.panelStates['CPR'].coronary!.currentCPRId = mainIndex.branchId; // 현재 갱신된 branchId
    },
    updateCPRStateIndexedStates(state, action: PayloadAction<IndexedStates>) {
      // const {branchId, pickPoints} = action.payload;
      Object.entries(action.payload).map(
        ([branchId, pickPoints]) =>
          (state.panelStates['CPR'].coronary!.state[Number(branchId)] =
            pickPoints),
      );
    },
    updateSwitchConfig(
      state,
      action: PayloadAction<Partial<Record<SwitchConfigs, boolean>>>,
    ) {
      state.uiConfig.layoutState.switchConfigs = {
        ...state.uiConfig.layoutState.switchConfigs,
        ...action.payload,
      };
    },
    //리스너에서 처리할거
    updateSwitchConfigTarget(
      state,
      action: PayloadAction<
        [Partial<Record<SwitchConfigs, boolean>>, PanelComponentKeyType]
      >,
    ) {
      const [switchConfig, target] = action.payload;
      state.uiConfig.layoutState.switchConfigs = {
        ...state.uiConfig.layoutState.switchConfigs,
        ...switchConfig,
      };
    },

    // 아주 으뜸이야.
    updateUIConfigVOIWindowLevel(
      state,
      action: PayloadAction< number>,
    ) {
      state.uiConfig.voi.windowLevel = action.payload;
    },
    updateUIConfigVOIWindowWidth(
      state,
      action: PayloadAction<number>,
    ) {
      state.uiConfig.voi.windowWidth = action.payload;
    },
    updateUIConfigVOIWindowLevelWidth(
      state,
      action: PayloadAction<[number, number]>,
    ) {
      const [windowLevel, windowWidth] = action.payload;
      state.uiConfig.voi.windowLevel = windowLevel;
      state.uiConfig.voi.windowWidth = windowWidth;
    },
    updateUIConfigVOIInvert(
      state,
      action: PayloadAction<boolean>,
    ){
      state.uiConfig.voi.invert = action.payload;
    },
    updateUIConfigPreset(
      state,
      action: PayloadAction<Record<string, PresetType>>,
    ) {
      const preset = action.payload;
      const key = Object.keys(preset)[0];
      if (key[0] === 'd') {
        delete state.uiConfig.preset[key.slice(1)];
      } else {
        state.uiConfig.preset = {
          ...state.uiConfig.preset,
          ...preset,
        };
      }
    },
    updateAnalysisDefault0(
      state,
      action: PayloadAction<Partial<Record<AnalysisType,AnalysisDefault>>>,
    ){
      state.analysisDefaults = {
        ...state.analysisDefaults,
        ...action.payload
      }
    },
    // fetchRecentlyUsedTool(state, action: PayloadAction<string>) {
    //   console.log('꺅'.repeat(Number(action.payload)))
    // },
    // fetchAnalysisDataSuccess(state, action: PayloadAction<any>) {
    //   state.analysisData = action.payload;
    //   console.log('성공이냐 ' + state.analysisData + ' , ' + state.error);
    //   state.error = undefined;
    // },
    // fetchAnalysisDataFailure(state, action: PayloadAction<string>) {
    //   state.analysisData = undefined;
    //   console.log('실패구나 ' + state.analysisData + ' , ' + state.error);
    //   state.error = action.payload;
    // },
    setCornerstoneInitialize(state, action: PayloadAction<boolean>) {
      state.cornerstoneConfig.initialized = action.payload;
    },
    updateCornerstoneRenderingEngineId(state, action: PayloadAction<string>) {
      const renderingEngineId = action.payload;
      if (
        !state.cornerstoneConfig.renderingEngineId.some(
          (id) => id === renderingEngineId,
        )
      )
        state.cornerstoneConfig.renderingEngineId.push(renderingEngineId);
    },
    resetLayoutConfig(state) {
      state.uiConfig.layoutState.layoutConfig = [];
      state.uiConfig.layoutState.switchConfigs = initialSwitchConfigType;
      state.uiConfig.layoutState.updateData = false;
    },

    resetSelectedSeriesLayout(state) {
      state.uiConfig.selectedLayoutConfig = EMPTY_DICOM_LAYOUT_CONFIG;
      state.uiConfig.layoutState.layoutConfig = [];
      state.uiConfig.layoutState.updateData = false;
      state.selectedAnalysis = null;
    },
    // setSynchPanelComponentKey: (
    //   state,
    //   action: PayloadAction<PanelComponentKeyType | undefined>,
    // ) => {
    //   state.uiConfig.layoutState.syncPanelComponentKey = state.toolStatesV2
    //     .global.toggles.ToggleSync
    //     ? action.payload
    //     : undefined;
    // },

    setSelectedPanelConfig: (state, action: PayloadAction<PanelConfig>) => {
      state.uiConfig.layoutState.selectedPanelConfig = action.payload;
    },
    // toolStateV2
    updatePanelTool: (
      state,
      action: PayloadAction<{
        panel: PanelType;
        id: ToolId;
        group: ToolCategoryType;
      }>,
    ) => {
      const { panel, id, group } = action.payload;
      state.toolStatesV2.panels[panel] = {
        activeToolId: id,
        activeGroup: group,
      };
    },
    updateGlobalTool: (state, action: PayloadAction<ToolId>) => {
      const id = action.payload;
      const toolDef = TOOL_REGISTRY.find((tool) => tool.id === id);
      if (toolDef && toolDef.behavior === 'toggle') {
        const toggles = state.toolStatesV2.global.toggles;
        const current = toggles[id] ?? getDefaultGlobalToggleValue(id);
        toggles[id] = !current;
      }
    },
    resetToolStatesV2: (state) => {
      const curstate: ToolStateV2 = {
        panels: {
          EmptyPanel: { activeToolId: undefined, activeGroup: undefined },
          DICOMPanel: { activeToolId: 'Scroll', activeGroup: 'navigation' },
          VRPanel: { activeToolId: 'Rotation', activeGroup: 'navigation' },
          Surface3DPanel: {
            activeToolId: 'Rotation',
            activeGroup: 'navigation',
          },
          CPRPanel: { activeToolId: 'Scroll', activeGroup: 'navigation' },
          CrossSectionPanel: {
            activeToolId: 'Scroll',
            activeGroup: 'navigation',
          },
          ReportPanel: { activeToolId: undefined, activeGroup: undefined },
          PlotPanel: { activeToolId: undefined, activeGroup: undefined },
        },
        global: {
          toggles: {
            ToggleAxes: configStore.get().annotationState.Axis,
            ToggleDICOMTags: false,
            ToggleMeasurementVisibility:
              configStore.get().annotationState.Measurement,
            ToggleAnnotations: configStore.get().annotationState.Annotations,
            ToggleSegmentationManager: false,
            TogglePatientData: configStore.get().annotationState.PatientInfo,
            ToggleDicomZoomSync: true,
            ToggleDicomPanSync: true,
            // ToggleSync: false,
            ToggleHeartCut: false,
            ToggleMIP: false,
            TogglePlaqueManual:false,
          },
        },
      };

      state.toolStatesV2 = JSON.parse(JSON.stringify(curstate));
    },
  },
});

export const {
  setSelectedAnalysis,
  setSelectedTool,
  setActivatedTool,
  // setRecentlyUsedTool,
  setCurrentLayout,
  setFullscreenPanelComponentKey,
  setSelectedPanelConfig,
  setLayoutStateLoading,
  setLayoutStateUpdateData,
  setSelectedPanelState,
  // setCommonSeriesInfo,
  updateSelectedLayoutPanelConfig,
  updatePanelStates,
  updatePanelStateThickness,
  updatePanelStateThicknessMethod,
  updatePanelStateZoom,
  // updatePanelStateWindowLevel,
  // updatePanelStateWindowWidth,
  // updatePanelStateWindowLevelWidth,
  updatePanelStateSpacing,
  updatePanelStateImageSize,
  updatePanelStateSliceIndex,
  updatePanelStatePosition,
  // updatePanelStatePreset,
  updatePanelStateSegmentation,
  updatePanelStateDirection,
  updatePanelStateChamberViewProps,
  updatePanelStateCoronary,
  updateCPRStateBatch,
  updateCPRStateMainIndex,
  updateCPRStateMainIndices,
  updateCPRStateIndexedState,
  updateCPRStateIndexedStates,
  updateSwitchConfig,
  updateSwitchConfigTarget,
  updateUIConfigVOIWindowWidth,
  updateUIConfigVOIWindowLevel,
  updateUIConfigVOIWindowLevelWidth,
  updateUIConfigVOIInvert,
  updateUIConfigPreset,
  updateAnalysisDefault0,
  setCornerstoneInitialize,
  updateCornerstoneRenderingEngineId,
  updatePanelTool,
  updateGlobalTool,
  resetToolStatesV2,
  // fetchRecentlyUsedTool,
  // fetchAnalysisDataSuccess,
  // fetchAnalysisDataFailure,
  // setThicknessMethod,
  // setThicknessValue,
  // setSynchPanelComponentKey,
  resetLayoutConfig,
  resetSelectedSeriesLayout,
} = analysisSlice.actions;
export default analysisSlice.reducer;
